import type { BufferGeometry } from 'three';
import type { LinearRGB } from '../../core/color';
import type { Ring } from '../../geo/types';
import { PATTERN } from '../../render/patterns/patternIds';
import { SURFACE } from '../mesh/attributes';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { boxSolid, horizontalCap } from '../fabric/solids';
import { gableRoof } from '../fabric/roofSolids';
import { gableWall, rectWall, type MasonryStyles } from '../fabric/landmarks/masonry';

/** Fitted to the elongated painted dike house; dimensions and hidden details are estimates. */
export const DIKE_HOUSE = {
  length: 24,
  width: 5,
  height: 4,
  pitch: (55 * Math.PI) / 180,
  chimneys: [-8, 0, 8],
} as const;

export function buildDikeHouse(
  wallColor: LinearRGB,
  roofColor: LinearRGB,
  edgeId: number,
  seed: number
): BufferGeometry {
  const builder = createMeshBuilder(),
    frame = { origin: [0, 0, 0] as const, yaw: 0 },
    half = DIKE_HOUSE.width / 2,
    long = DIKE_HOUSE.length / 2,
    ridge = DIKE_HOUSE.height + half * Math.tan(DIKE_HOUSE.pitch),
    dark: LinearRGB = [roofColor[0] * 0.2, roofColor[1] * 0.2, roofColor[2] * 0.2],
    timber: LinearRGB = [wallColor[0] * 0.35, wallColor[1] * 0.28, wallColor[2] * 0.2],
    brickColor: LinearRGB = [wallColor[0] * 0.65, wallColor[1] * 0.42, wallColor[2] * 0.3],
    style = { edgeId, seed, strokeAngle: 0, extent: [0, 0] as const },
    wall = { ...style, color: wallColor, surface: SURFACE.wood, pattern: PATTERN.timberFront1538 },
    roof = { ...style, color: roofColor, surface: SURFACE.roofThatch },
    styles: MasonryStyles = {
      wall,
      roof,
      opening: { ...style, color: dark, surface: SURFACE.linework },
      glazing: { reveal: wall },
    },
    ring: Ring = [
      [-long, -half],
      [long, -half],
      [long, half],
      [-long, half],
    ],
    window = (at: number, sill = 1.1, top = 2.2): Ring => [
      [at - 0.35, sill],
      [at + 0.35, sill],
      [at + 0.35, top],
      [at - 0.35, top],
    ];
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % ring.length]!,
      end = side % 2 === 1,
      width = end ? DIKE_HOUSE.width : DIKE_HOUSE.length,
      holes = end ? [window(width / 2)] : [2.8, 7.4, 16.6, 21.2].map((at) => window(at));
    if (side === 0)
      holes.push([
        [11.5, 0.1],
        [12.5, 0.1],
        [12.5, 2.4],
        [11.5, 2.4],
      ]);
    if (end)
      gableWall(builder, styles, frame, [from, to], { eaveY: DIKE_HOUSE.height, apexY: ridge }, [
        ...holes,
        window(width / 2, 4.4, 5.1),
      ]);
    else rectWall(builder, styles, frame, [from, to], [0, DIKE_HOUSE.height], holes);
  });
  const timberStyle = {
    ...style,
    color: timber,
    surface: SURFACE.wood,
    pattern: PATTERN.timberFront1538,
  };
  // The door sits inside the reveal, just ahead of the dark backing plane.
  boxSolid(builder, timberStyle, frame, [-0.49, 0.11, half - 0.24], [0.49, 2.39, half - 0.21], {
    top: true,
    bottom: true,
  });
  gableRoof(builder, { wall, roof }, frame, {
    u0: -long,
    u1: long,
    v0: -half,
    v1: half,
    eaveY: DIKE_HOUSE.height,
    ridgeY: ridge,
    ends: { start: false, end: false },
  });
  horizontalCap(builder, wall, frame, ring, 0.01, true);
  const brick = {
      ...style,
      color: brickColor,
      surface: SURFACE.brick,
      pattern: PATTERN.houseBrick1538,
    },
    flue = { ...style, color: dark, surface: SURFACE.linework },
    top = ridge + 1.5;
  // The woodcut corroborates three stacks; their appearance in 1538 remains an estimate.
  for (const u of DIKE_HOUSE.chimneys) {
    boxSolid(builder, brick, frame, [u - 0.34, ridge - 0.4, -0.37], [u + 0.34, top - 0.25, 0.37], {
      top: true,
      bottom: true,
    });
    for (const [a, b] of [
      [
        [u - 0.45, top - 0.25, -0.48],
        [u + 0.45, top, -0.25],
      ],
      [
        [u - 0.45, top - 0.25, 0.25],
        [u + 0.45, top, 0.48],
      ],
      [
        [u - 0.45, top - 0.25, -0.25],
        [u - 0.24, top, 0.25],
      ],
      [
        [u + 0.24, top - 0.25, -0.25],
        [u + 0.45, top, 0.25],
      ],
    ] as const)
      boxSolid(builder, brick, frame, a, b, { top: true, bottom: true });
    boxSolid(builder, flue, frame, [u - 0.24, top - 0.24, -0.25], [u + 0.24, top - 0.23, 0.25], {
      top: true,
      bottom: true,
    });
  }
  return builder.build();
}
